import { useState, useCallback } from 'react';
import { RenewalFormData } from '../components/Academic/AcademicRenewalForm';
import { CancellationFormData } from '../components/Academic/AcademicCancellationForm';
import academicExpirationService from '@/services/AcademicExpirationService';

interface AcademicFeedbackMessage {
  name: string;
  email: string;
  organization: string;
  message: string;
  subject: string;
  startedAt: number;
  website: string;
}

interface UserInfo {
  name?: string;
  email: string;
  organization?: string;
  /** Plan from Cognito (e.g. cognito:groups joined) */
  plan?: string;
}

export const useAcademicFeedback = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackSentMessage, setFeedbackSentMessage] = useState('');
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const formatRenewalFeedback = (formData: RenewalFormData, userInfo: UserInfo): string => {
    const reasonLabels: Record<string, string> = {
      'data-quality': 'The data quality meets my research needs',
      'essential-project': 'DISGENET is essential for my current projects',
      'regular-use': 'I use DISGENET regularly',
      'institutional-requirement': 'Institutional / funding requirement',
      other: 'Other',
    };

    const applicationTypeLabels: Record<string, string> = {
      'drug-target': 'Drug R&D (drug target prioritization, drug repurposing, etc)',
      'genomic-variant': 'Genomic variant interpretation and analysis',
      biomarker: 'Biomarker research (discovery, validation)',
      instructors: 'Teaching and training (as instructors/professors)',
      students: 'Teaching and training (as students)',
      bioinformatics: 'Method development (bioinformatic, AI, ML, etc)',
      other: 'Other',
    };

    const accessLabels: Record<string, string> = {
      'web-interface': 'Web interface',
      'rest-api': 'REST API',
      'disgenet-package': 'disgenet2r R package',
      cytoscape: 'DISGENET Cytoscape App',
      aida: 'AIDA, the DISGENET assistant',
    };

    const usefulnessLabels = [
      'Not useful',
      'Slightly useful',
      'Moderately useful',
      'Very useful',
      'Extremely useful',
    ];

    const usageFreq = Array.isArray(formData.usageFrequency)
      ? formData.usageFrequency
      : [formData.usageFrequency].filter(Boolean);
    const usageFreqText = usageFreq.map(v => applicationTypeLabels[v] || v).join('; ');
    const accessArr = Array.isArray(formData.access) ? formData.access : [formData.access].filter(Boolean);
    const accessText = accessArr.map(v => accessLabels[v] || v).join('; ');
    const otherSpecify =
      formData.usageFrequency?.includes('other') && formData.applicationTypeOther
        ? ` (${formData.applicationTypeOther})`
        : '';

    return `
ACADEMIC RENEWAL FEEDBACK

User Information:
- Name: ${userInfo.name || 'Not provided'}
- Email: ${userInfo.email}
- Organization: ${userInfo.organization || 'Not provided'}

Feedback Responses:
1. Main reason for renewal: ${reasonLabels[formData.mainReason] || formData.mainReason}
2. Application type: ${usageFreqText}${otherSpecify}
3. Access: ${accessText}
4. Usefulness rating: ${formData.usefulness}/5 (${usefulnessLabels[formData.usefulness - 1]})
5. Suggestions: ${formData.suggestions || 'No suggestions provided'}
${formData.marketingConsent != null ? `6. Consent to use responses for marketing/communication: ${formData.marketingConsent === 'yes' ? 'Yes, I agree' : 'No, I do not agree'}` : ''}

This feedback was submitted as part of the academic account renewal process.
    `.trim();
  };

  const formatCancellationFeedback = (formData: CancellationFormData, userInfo: UserInfo): string => {
    const reasonLabels: Record<string, string> = {
      'no-longer-need': 'I no longer need DISGENET for my research',
      'did-not-use-enough': 'I did not use DISGENET enough',
      'data-coverage-issues': 'Data or coverage did not meet my expectations',
      'pricing-constraints': 'Pricing or licensing constraints',
      'technical-issues': 'Technical or usability issues',
      other: 'Other',
    };

    const applicationTypeLabels: Record<string, string> = {
      'drug-target': 'Drug R&D (drug target prioritization, drug repurposing, etc)',
      'genomic-variant': 'Genomic variant interpretation and analysis',
      biomarker: 'Biomarker research (discovery, validation)',
      instructors: 'Teaching and training (as instructors/professors)',
      students: 'Teaching and training (as students)',
      bioinformatics: 'Method development (bioinformatic, AI, ML, etc)',
      other: 'Other',
    };

    const accessLabels: Record<string, string> = {
      'web-interface': 'Web interface',
      'rest-api': 'REST API',
      'disgenet-package': 'disgenet2r R package',
      cytoscape: 'DISGENET Cytoscape App',
      aida: 'AIDA, the DISGENET assistant',
    };

    const usefulnessLabels = [
      'Not useful',
      'Slightly useful',
      'Moderately useful',
      'Very useful',
      'Extremely useful',
    ];

    const usageFreq = Array.isArray(formData.usageFrequency)
      ? formData.usageFrequency
      : [formData.usageFrequency].filter(Boolean);
    const usageFreqText = usageFreq.map(v => applicationTypeLabels[v] || v).join('; ');
    const accessArr = Array.isArray(formData.access) ? formData.access : [formData.access].filter(Boolean);
    const accessText = accessArr.map(v => accessLabels[v] || v).join('; ');
    const otherSpecify =
      formData.usageFrequency?.includes('other') && formData.applicationTypeOther
        ? ` (${formData.applicationTypeOther})`
        : '';

    return `
ACADEMIC CANCELLATION FEEDBACK

User Information:
- Name: ${userInfo.name || 'Not provided'}
- Email: ${userInfo.email}
- Organization: ${userInfo.organization || 'Not provided'}

Feedback Responses:
1. Main reason for cancellation: ${reasonLabels[formData.mainReason] || formData.mainReason}
2. Application type: ${usageFreqText}${otherSpecify}
3. Access: ${accessText}
4. Usefulness rating: ${formData.usefulness}/5 (${usefulnessLabels[formData.usefulness - 1]})
5. Suggestions: ${formData.suggestions || 'No suggestions provided'}
${formData.marketingConsent != null ? `6. Consent to use responses for marketing/communication: ${formData.marketingConsent === 'yes' ? 'Yes, I agree' : 'No, I do not agree'}` : ''}

This feedback was submitted as part of the academic account cancellation process.
    `.trim();
  };

  const submitRenewalFeedback = useCallback(
    async (
      formData: RenewalFormData,
      userInfo: UserInfo,
      options?: {
        renewalSurveyToken?: string | null;
        reactivateDisabledAccount?: boolean;
      },
    ) => {
      setIsSubmitting(true);
      setFeedbackError(null);
      setFeedbackSentMessage('');

      const contactUrl = process.env.REACT_APP_CONTACT_API_URL
        ? `${process.env.REACT_APP_CONTACT_API_URL}/contact`
        : 'https://5kdsze9xs4.execute-api.eu-central-1.amazonaws.com/contact';
      const renewalStoreUrl = process.env.REACT_APP_RENEWAL_RESPONSES_API_URL
        ? `${process.env.REACT_APP_RENEWAL_RESPONSES_API_URL}/renewal-responses/store`
        : '';

      const messageInfo: AcademicFeedbackMessage = {
        name: userInfo.name || 'Academic User',
        email: userInfo.email,
        organization: userInfo.organization || 'Not specified',
        subject: 'Academic Renewal Feedback',
        message: formatRenewalFeedback(formData, userInfo),
        // Required by contact API anti-bot validation.
        startedAt: Date.now(),
        website: '',
      };

      const s3Payload = {
        userIdentifier: userInfo.email,
        submittedAt: new Date().toISOString(),
        formAnswers: formData,
        metadata: {
          plan: userInfo.plan ?? 'academic',
          name: userInfo.name,
          organization: userInfo.organization,
        },
      };

      const contactPromise = fetch(contactUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageInfo),
      }).then(r => {
        if (!r.ok) throw new Error('Contact request failed');
        return true;
      });

      const s3Promise = renewalStoreUrl
        ? fetch(renewalStoreUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(s3Payload),
          }).then(r => {
            if (!r.ok) throw new Error('Store request failed');
            return true;
          })
        : Promise.resolve(null);

      const results = await Promise.allSettled([contactPromise, s3Promise]);
      const [contactResult, s3Result] = results;

      if (contactResult.status === 'rejected') {
        console.error('Renewal feedback (Freshdesk) submission error:', contactResult.reason);
        setFeedbackError('Failed to send feedback to support system');
        setIsSubmitting(false);
        return false;
      }

      if (s3Result.status === 'rejected') {
        console.error('Renewal response S3 store error:', s3Result.reason);
      }

      const renewResult = await academicExpirationService.renewAcademicAccess(userInfo.email, formData, {
        renewalSurveyToken: options?.renewalSurveyToken || undefined,
        reactivateDisabledAccount: options?.reactivateDisabledAccount === true,
      });

      if (!renewResult.success) {
        setFeedbackError(
          renewResult.message || renewResult.error || 'Failed to start academic renewal process',
        );
        setIsSubmitting(false);
        return false;
      }

      setFeedbackSentMessage('Renewal feedback sent successfully!');
      setIsSubmitting(false);
      return true;
    },
    [],
  );

  const submitCancellationFeedback = useCallback(
    async (formData: CancellationFormData, userInfo: UserInfo) => {
      setIsSubmitting(true);
      setFeedbackError(null);
      setFeedbackSentMessage('');

      try {
        const messageInfo: AcademicFeedbackMessage = {
          name: userInfo.name || 'Academic User',
          email: userInfo.email,
          organization: userInfo.organization || 'Not specified',
          subject: 'Academic Cancellation Feedback',
          message: formatCancellationFeedback(formData, userInfo),
          // Required by contact API anti-bot validation.
          startedAt: Date.now(),
          website: '',
        };

        const response = await fetch(
          `${process.env.REACT_APP_CONTACT_API_URL}/contact` ||
            'https://5kdsze9xs4.execute-api.eu-central-1.amazonaws.com/contact',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(messageInfo),
          },
        );

        if (!response.ok) throw new Error('Network error');

        setFeedbackSentMessage('Cancellation feedback sent successfully!');
        return true;
      } catch (error) {
        console.error('Cancellation feedback submission error:', error);
        setFeedbackError('Failed to send feedback to support system');
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  const resetFeedback = useCallback(() => {
    setFeedbackSentMessage('');
    setFeedbackError(null);
  }, []);

  return {
    isSubmitting,
    feedbackSentMessage,
    feedbackError,
    setFeedbackError,
    submitRenewalFeedback,
    submitCancellationFeedback,
    resetFeedback,
  };
};
